import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import {
  Datagrid,
  DatagridAction,
  DatagridColumn,
  DatagridSortDirection,
  RowCondition,
} from '@zipari/shared-ds-util-datagrid';
import { changeSort, randomString } from '@zipari/shared-util-common';
import { checkInputsForText } from '../../design-system.helper';
import { getIconParentId } from './datagrid.helper';
import { DesignSystemService } from '../../design-system.service';

@Component({
  selector: 'datagrid',
  styleUrls: ['./datagrid.component.scss'],
  templateUrl: './datagrid.component.html',
})
export class DatagridComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() clearSelectedRows: boolean;
  @Input() config: Datagrid;
  @Input() data: object[];
  @Input() isLoading: boolean;
  @Input() selectableRows: boolean;
  @Input() sortOrder: string; // which property is the grid sorted by
  @Input() condition: RowCondition;
  @Input() hasAnErrorOccurred = false;
  @Input() footerDetail: object;
  @Output() actionClicked = new EventEmitter<DatagridAction>();
  @Output() datagridRowClicked = new EventEmitter<object>();
  @Output() datagridRowSelected = new EventEmitter<any>();
  @Output() datagridSsoRowSelected = new EventEmitter<any>();
  @Output() selectionActionClicked = new EventEmitter<object[]>();
  @Output() sortClicked: EventEmitter<string> = new EventEmitter();
  @Output() sortHeaderClicked: EventEmitter<string> = new EventEmitter();
  /**
   * @deprecated
   * @param sort is deprecated in favor of sortClicked. Please remove from your code
   */
  @Output() sort: EventEmitter<string> = new EventEmitter();
  @ViewChild('datagridBody') datagridBody;
  @ViewChild('datagridShadowLeft') datagridShadowLeft;
  @ViewChild('datagridShadowRight') datagridShadowRight;

  defaultRows: object[];
  isScrollable: boolean;
  rows: object[];
  selectedRows: object[] = [];
  sortDirections = DatagridSortDirection;
  dataGridId: string = randomString();
  public selectedRowsTitle = 'Rows Selected';
  public limit = 2; // default limit to show the number of items in expanding list column
  public expandedCards: { [key: number]: boolean } = {};
  private eventListener: () => void;

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private designSystemService: DesignSystemService,
  ) {}

  public get rowsSelectedStatus(): string {
    return this.config.selectedRowsTitle || this.selectedRowsTitle;
  }

  @HostListener('scroll', ['$event'])
  onScroll($event) {
    const scrollLeft = $event.target.scrollLeft;
    const scrollWidth = $event.target.scrollWidth;
    const clientWidth = $event.target.clientWidth;

    if (scrollLeft > 0) {
      this.renderer.setStyle(
        this.datagridShadowLeft.nativeElement,
        'opacity',
        '1',
      );
      this.renderer.setStyle(
        this.datagridShadowLeft.nativeElement,
        'left',
        `${scrollLeft}px`,
      );
    } else {
      this.renderer.setStyle(
        this.datagridShadowLeft.nativeElement,
        'opacity',
        '0',
      );
    }

    if (scrollLeft + clientWidth === scrollWidth) {
      this.renderer.setStyle(
        this.datagridShadowRight.nativeElement,
        'opacity',
        '0',
      );
    } else {
      this.renderer.setStyle(
        this.datagridShadowRight.nativeElement,
        'opacity',
        '1',
      );
      this.renderer.setStyle(
        this.datagridShadowRight.nativeElement,
        'right',
        `-${scrollLeft}px`,
      );
    }
  }

  ngOnInit() {
    const [config, data, footerDetail] = checkInputsForText([
      this.config,
      this.data,
      this.footerDetail,
    ]);

    this.initializeSortOrder(config as Datagrid);

    this.config = config as Datagrid;
    this.data = data as object[];
    this.footerDetail = footerDetail;
    this.rows = this.getData();
    this.defaultRows = this.getData();
    this.isScrollable = this.config.isScrollable && this.rows.length > 0;
    this.isLoading = !!this.isLoading;
  }

  ngOnDestroy() {
    if (this.eventListener) {
      this.eventListener();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) this.rows = this.getData();
    if (changes.clearSelectedRows) this.selectedRows = [];
    // Update isScrollable here to account for delays in receiving data
    this.isScrollable = this.config.isScrollable && this.rows.length > 0;
  }

  /** `getBoundingClientRect()` gives improper position when inside tabs. */
  ngAfterViewInit() {
    if (this.unscrollableFillWindow(this.config)) {
      setTimeout(() => window.dispatchEvent(new Event('resize')));
    }

    this.eventListener = this.designSystemService.listenForAttributeUpdates(
      this,
      this.elementRef.nativeElement,
      () => {
        this.rows = this.getData();
      },
    );
  }

  initializeSortOrder(config: Datagrid): void {
    if (typeof this.config === 'string') {
      const sortColumn = config?.initSort?.columnProp;
      const sortDirection = config?.initSort?.sortDirection;

      if (sortColumn) {
        this.sortOrder = `${
          sortDirection?.toLowerCase().includes('desc') ? '-' : ''
        }${sortColumn}`;
      }
    }
  }

  unscrollableFillWindow(config: Datagrid): boolean {
    return config?.fillWindow && !config.isScrollable;
  }
  /** The maximum height of the datagrid body should be set to show the entire datagrid page. */
  setDatagridBodyHeight() {
    this.renderer.setStyle(
      this.datagridBody.nativeElement,
      'max-height',
      `${
        window.innerHeight -
        this.datagridBody.nativeElement.getBoundingClientRect().top
      }px`,
    );
  }

  viewDetail(row, columnProp: string, routeToProp?: string) {
    if (routeToProp) {
      this.datagridRowSelected.emit({ uniqueCellProp: routeToProp, ...row });
    } else {
      this.datagridRowSelected.emit({ uniqueCellProp: columnProp, ...row });
    }
  }

  viewSSODetail(row, columnRoute: string, columnOptions) {
    if (columnOptions.length > 0) {
      this.datagridSsoRowSelected.emit({
        ...row,
        route: columnRoute,
        options: columnOptions,
      });
    } else {
      this.datagridSsoRowSelected.emit({
        ...row,
        route: columnRoute,
        options: [],
      });
    }
  }

  onActionClicked(row: any, action: string, clickEvent?: Event) {
    const targetElementId: string = clickEvent
      ? getIconParentId(clickEvent)
      : '';

    this.actionClicked.emit({ row, action, targetElementId });
  }

  // SORTING
  sortByProperty(prop: string) {
    this.sortClicked.emit(prop);
    this.sort.emit(prop); // keeping for backward compatibility
  }

  handleColumnSortByClick(event: Event, column: DatagridColumn) {
    event.stopPropagation();
    event.preventDefault();
    const propToSortBy = this.getSortingProperty(column);

    column.sortable && this.sortByProperty(propToSortBy);
    this.sortOrder = this.sortOrder?.includes(propToSortBy)
      ? changeSort(this.sortOrder)
      : changeSort(propToSortBy);
    this.sortHeaderClicked.emit(this.sortOrder);
  }

  getSortingProperty(column: DatagridColumn) {
    return column.sortByProp ? column.sortByProp : column.prop;
  }

  getData() {
    return this.data
      ? [...(typeof this.data === 'string' ? JSON.parse(this.data) : this.data)]
      : [];
  }
}
